import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const currentUser = (() => {
  try { return JSON.parse(localStorage.getItem('doorly_admin_user') || '{}'); }
  catch { return {}; }
})();
const isAdmin = currentUser?.role === 'admin';

const fmt = (cents) =>
  '$' + (Number(cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct = (pct) => (Number(pct) || 0).toFixed(1) + '%';

const PRESETS = [
  { value: 'this_month',   label: 'This Month' },
  { value: 'last_month',   label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'this_year',    label: 'This Year' },
  { value: 'last_year',    label: 'Last Year' },
  { value: 'custom',       label: 'Custom Range' },
];

const INCOME_CATEGORIES = [
  'Program Fees', 'Origination Fees', 'Credit Report Fees',
  'Appraisal Package Fees', 'Real Estate Spread',
  'Service Release Premium', 'Real Estate Commission',
];
const COR_CATEGORIES = [
  'Partner Commissions', 'Acquisition Facility Fee', 'Document Prep Fees',
  'Appraisal & Inspection Fees', 'Xactus Credit Report', 'Compliance Ease',
  'Real Broker Split (15%)',
];
const OPEX_CATEGORIES = [
  'Technology & SaaS', 'Marketing & Advertising', 'Professional Services',
  'W2 Payroll', 'Office & Admin', 'Legal',
];

export default function AdminPL() {
  const [plData, setPlData]         = useState(null);
  const [plPreset, setPlPreset]     = useState('this_year');
  const [plEntity, setPlEntity]     = useState('consolidated');
  const [plStart, setPlStart]       = useState('');
  const [plEnd, setPlEnd]           = useState('');
  const [plLoading, setPlLoading]   = useState(false);
  const [plSource, setPlSource]     = useState('doorly'); // 'doorly' | 'xero'
  const [xeroStatus, setXeroStatus] = useState(null);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [entryForm, setEntryForm]   = useState({
    month: new Date().toISOString().slice(0, 7) + '-01',
    category: 'Technology & SaaS',
    subcategory: '',
    amount_cents: '',
    direction: 'expense',
    notes: '',
    entity: 'go_doorly',
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` };

  const fetchXeroStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/xero/status`, { headers });
      setXeroStatus(res.data);
    } catch (err) {
      setXeroStatus(null);
    }
  };

  const fetchPL = async (preset, start, end, entity = plEntity, source = plSource) => {
    setPlLoading(true);
    try {
      let data;
      if (source === 'xero') {
        // Xero endpoint takes start/end only. For non-custom presets we resolve
        // the date range client-side so the existing preset buttons "just work."
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        let startDate, endDate;
        switch (preset) {
          case 'this_month':   startDate = new Date(year, month, 1);     endDate = new Date(year, month + 1, 0); break;
          case 'last_month':   startDate = new Date(year, month - 1, 1); endDate = new Date(year, month, 0);     break;
          case 'this_quarter': { const q  = Math.floor(month / 3); startDate = new Date(year, q * 3, 1); endDate = new Date(year, q * 3 + 3, 0); break; }
          case 'last_quarter': { const lq = Math.floor(month / 3) - 1; startDate = new Date(year, lq * 3, 1); endDate = new Date(year, lq * 3 + 3, 0); break; }
          case 'last_year':    startDate = new Date(year - 1, 0, 1);    endDate = new Date(year - 1, 11, 31);   break;
          default:             startDate = new Date(year, 0, 1);        endDate = new Date(year, 11, 31);       break;
        }
        const s = preset === 'custom' ? start : startDate.toISOString().split('T')[0];
        const e = preset === 'custom' ? end   : endDate.toISOString().split('T')[0];
        const res = await axios.get(`${API}/api/xero/pl?start=${s}&end=${e}`, { headers });
        data = res.data;
      } else {
        const params = preset === 'custom'
          ? `start=${start}&end=${end}&entity=${entity}`
          : `preset=${preset}&entity=${entity}`;
        const res = await axios.get(`${API}/api/admin/agents/finance/pl?${params}`, { headers });
        data = res.data;
      }
      setPlData(data);
    } catch (err) {
      console.error('P&L fetch failed:', err.message);
    } finally {
      setPlLoading(false);
    }
  };

  // Initial load: status + P&L in parallel.
  useEffect(() => {
    fetchXeroStatus();
    fetchPL(plPreset, plStart, plEnd, plEntity, plSource);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Xero OAuth post-flow handling. Two paths:
  //   1) Popup window → /callback posts a message → we listen here (primary).
  //   2) Same-tab fallback → /callback redirects with ?xero=connected|error.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('xero') === 'connected') {
      window.history.replaceState({}, '', window.location.pathname);
      fetchXeroStatus();
    } else if (params.get('xero') === 'error') {
      window.history.replaceState({}, '', window.location.pathname);
      alert('Xero connection failed — please try again');
    }
    const handleMessage = (event) => {
      if (event.data?.type === 'xero_connected') fetchXeroStatus();
      if (event.data?.type === 'xero_error') alert('Xero connection failed — please try again');
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>Finance</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Profit &amp; Loss</h1>
        </div>

        {/* Xero connection banner */}
        <div style={{
          background: xeroStatus?.connected ? '#f0fdf4' : '#fef9c3',
          border: `1px solid ${xeroStatus?.connected ? '#86efac' : '#fde68a'}`,
          borderRadius: 10, padding: '12px 16px', marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: xeroStatus?.connected ? '#166534' : '#854d0e', marginBottom: 2 }}>
              {xeroStatus?.connected ? `✓ Xero Connected — ${xeroStatus.tenant_name}` : '⚠️ Xero Not Connected'}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>
              {xeroStatus?.connected
                ? `Last synced: ${xeroStatus.last_synced_at ? new Date(xeroStatus.last_synced_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never'}`
                : 'Connect Xero to sync audited P&L figures'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {xeroStatus?.connected ? (
              <>
                <div style={{ display: 'flex', gap: 2, background: '#f3f4f6', borderRadius: 8, padding: 3 }}>
                  {[
                    { value: 'doorly', label: 'Doorly' },
                    { value: 'xero',   label: 'Xero (Audited)' },
                  ].map(s => (
                    <button key={s.value}
                      onClick={() => { setPlSource(s.value); fetchPL(plPreset, plStart, plEnd, plEntity, s.value); }}
                      style={{
                        padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, border: 'none',
                        background: plSource === s.value ? '#fff' : 'transparent',
                        color: plSource === s.value ? '#111827' : '#6b7280',
                        cursor: 'pointer',
                        boxShadow: plSource === s.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      }}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm('Disconnect Xero? This will clear cached P&L data.')) return;
                    try {
                      await axios.delete(`${API}/api/xero/disconnect`, { headers });
                      setXeroStatus(null);
                      setPlSource('doorly');
                      fetchPL(plPreset, plStart, plEnd, plEntity, 'doorly');
                    } catch (err) {
                      alert(err?.response?.data?.error || err.message || 'Disconnect failed.');
                    }
                  }}
                  style={{ background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 500, cursor: 'pointer' }}>
                  Disconnect
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  // Popup keeps DoorlyDesk open in the parent tab. The popup
                  // posts back to us on success/error (see message listener
                  // above). `popup.closed` polling is a fallback in case the
                  // postMessage is missed.
                  const popup = window.open(
                    `${API}/api/xero/connect`,
                    'xero-oauth',
                    'width=600,height=700,scrollbars=yes'
                  );
                  if (!popup) {
                    alert('Pop-up blocked — allow pop-ups for this site and try again.');
                    return;
                  }
                  const timer = setInterval(() => {
                    if (popup.closed) {
                      clearInterval(timer);
                      fetchXeroStatus();
                    }
                  }, 500);
                }}
                style={{ background: '#1AB4D7', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                Connect Xero →
              </button>
            )}
          </div>
        </div>

        {/* Entity breakdown */}
        <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 12 }}>
          {[
            { value: 'consolidated', label: 'Consolidated' },
            { value: 'go_doorly', label: 'Go Doorly LLC' },
            { value: 'doorly_realty', label: 'Doorly Realty LLC' },
          ].map(e => (
            <button key={e.value}
              onClick={() => { setPlEntity(e.value); fetchPL(plPreset, plStart, plEnd, e.value); }}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none',
                background: plEntity === e.value ? '#111827' : 'transparent',
                color: plEntity === e.value ? '#fff' : '#6b7280', cursor: 'pointer',
              }}>
              {e.label}
            </button>
          ))}
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {PRESETS.map(p => (
              <button key={p.value}
                onClick={() => { setPlPreset(p.value); if (p.value !== 'custom') fetchPL(p.value, '', ''); }}
                style={{
                  padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid #e5e7eb',
                  background: plPreset === p.value ? '#3a6ad9' : '#fff',
                  color: plPreset === p.value ? '#fff' : '#374151', cursor: 'pointer',
                }}>
                {p.label}
              </button>
            ))}
          </div>

          {plPreset === 'custom' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="date" value={plStart} onChange={e => setPlStart(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }} />
              <span style={{ color: '#6b7280', fontSize: 12 }}>to</span>
              <input type="date" value={plEnd} onChange={e => setPlEnd(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }} />
              <button onClick={() => fetchPL('custom', plStart, plEnd)} disabled={!plStart || !plEnd}
                style={{ padding: '6px 14px', borderRadius: 8, background: '#3a6ad9', color: '#fff', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Apply
              </button>
            </div>
          )}

          {isAdmin && (
            <button onClick={() => setShowAddEntry(s => !s)}
              style={{ padding: '7px 16px', borderRadius: 8, background: '#fff', color: '#374151', border: '1px solid #e5e7eb', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              + Add Entry
            </button>
          )}
        </div>

        {/* Manual entry form */}
        {showAddEntry && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 22px', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 14 }}>Add Manual P&amp;L Entry</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
              {[
                { key: 'month', label: 'Month', type: 'date' },
                { key: 'subcategory', label: 'Line Item Name', type: 'text' },
                { key: 'notes', label: 'Notes', type: 'text' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>{f.label}</label>
                  <input type={f.type} value={entryForm[f.key]}
                    onChange={e => setEntryForm(ef => ({ ...ef, [f.key]: e.target.value }))}
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, boxSizing: 'border-box' }} />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>Category</label>
                <select value={entryForm.category}
                  onChange={e => setEntryForm(ef => ({ ...ef, category: e.target.value, direction: INCOME_CATEGORIES.includes(e.target.value) ? 'income' : 'expense' }))}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, boxSizing: 'border-box' }}>
                  <optgroup label="Income">{INCOME_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</optgroup>
                  <optgroup label="Cost of Revenue">{COR_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</optgroup>
                  <optgroup label="Operating Expenses">{OPEX_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</optgroup>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>Amount ($)</label>
                <input type="number" value={entryForm.amount_cents ? entryForm.amount_cents / 100 : ''}
                  onChange={e => setEntryForm(ef => ({ ...ef, amount_cents: Math.round(parseFloat(e.target.value || 0) * 100) }))}
                  placeholder="0.00"
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>Entity</label>
                <select value={entryForm.entity}
                  onChange={e => setEntryForm(ef => ({ ...ef, entity: e.target.value }))}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, boxSizing: 'border-box' }}>
                  <option value="go_doorly">Go Doorly LLC</option>
                  <option value="doorly_realty">Doorly Realty LLC</option>
                  <option value="consolidated">Consolidated</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowAddEntry(false)}
                style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={async () => {
                if (!entryForm.month || !entryForm.category || !entryForm.amount_cents || !entryForm.direction) {
                  alert('Month, section, and a non-zero amount are required.');
                  return;
                }
                try {
                  await axios.post(`${API}/api/admin/agents/finance/pl/entry`, entryForm, { headers });
                  setShowAddEntry(false);
                  fetchPL(plPreset, plStart, plEnd);
                } catch (err) {
                  alert(err?.response?.data?.error || err.message || 'Failed to add entry.');
                }
              }}
                style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Add Entry
              </button>
            </div>
          </div>
        )}

        {plLoading && (
          <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>Loading P&amp;L...</div>
        )}

        {/* P&L Statement */}
        {!plLoading && plData && (() => {
          const { sections, totals, period } = plData;
          const SectionRow = ({ label, amount, bold, indent, borderTop, derived, notes }) => (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: indent ? '5px 22px 5px 36px' : '8px 22px',
              borderTop: borderTop ? '1px solid #e5e7eb' : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <span style={{ fontSize: bold ? 13 : 12, fontWeight: bold ? 700 : 400, color: bold ? '#111827' : '#374151' }}>{label}</span>
                {derived && (
                  <span style={{ fontSize: 9, fontWeight: 600, background: '#eff6ff', color: '#1e40af', borderRadius: 4, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>auto</span>
                )}
                {notes && !bold && (
                  <span style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic' }}>{notes}</span>
                )}
              </div>
              <div style={{ fontSize: bold ? 14 : 12, fontWeight: bold ? 700 : 400, color: amount < 0 ? '#991b1b' : '#111827', fontFamily: 'monospace', whiteSpace: 'nowrap', paddingLeft: 12 }}>
                {amount < 0 ? `(${fmt(Math.abs(amount))})` : fmt(amount)}
              </div>
            </div>
          );
          return (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ background: '#111827', padding: '20px 22px' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 2 }}>
                  Profit &amp; Loss Statement
                  {plData?.source === 'xero' && (
                    <span style={{ background: '#1AB4D7', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '2px 8px', marginLeft: 8, verticalAlign: 'middle', letterSpacing: '0.05em' }}>
                      XERO
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>
                  Go Doorly, LLC · {new Date(period.start).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} – {new Date(period.end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
                {plData?.source === 'xero' && plData?.cached && (
                  <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                    Cached · fetched {new Date(plData.fetched_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </div>
                )}
              </div>
              {plData.loanCount > 0 && (
                <div style={{ padding: '8px 22px', background: '#f9fafb', borderBottom: '1px solid #f3f4f6', fontSize: 12, color: '#6b7280' }}>
                  Based on {plData.loanCount} closed loan{plData.loanCount !== 1 ? 's' : ''} in this period
                </div>
              )}

              {/* Income */}
              <div style={{ padding: '12px 0 4px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ padding: '4px 22px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Income</div>
                {(sections['Income'] || []).map((line, i) => (
                  <SectionRow key={i} label={line.subcategory || line.category} amount={line.amount_cents} indent derived={line.derived} notes={line.notes} />
                ))}
                {(!sections['Income'] || sections['Income'].length === 0) && (
                  <div style={{ padding: '4px 36px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No income entries for this period</div>
                )}
                <SectionRow label="Total Income" amount={totals.income} bold borderTop />
              </div>

              {/* Cost of Revenue */}
              <div style={{ padding: '12px 0 4px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ padding: '4px 22px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Cost of Revenue</div>
                {(sections['Cost of Revenue'] || []).map((line, i) => (
                  <SectionRow key={i} label={line.subcategory || line.category} amount={line.amount_cents} indent derived={line.derived} notes={line.notes} />
                ))}
                {(!sections['Cost of Revenue'] || sections['Cost of Revenue'].length === 0) && (
                  <div style={{ padding: '4px 36px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No cost of revenue entries for this period</div>
                )}
                <SectionRow label="Total Cost of Revenue" amount={totals.costOfRevenue} bold borderTop />
              </div>

              {/* Gross Profit */}
              <div style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 22px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Gross Profit</div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: totals.grossProfit >= 0 ? '#166534' : '#991b1b', fontFamily: 'monospace' }}>
                      {totals.grossProfit < 0 ? `(${fmt(Math.abs(totals.grossProfit))})` : fmt(totals.grossProfit)}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{fmtPct(totals.grossMargin)} margin</div>
                  </div>
                </div>
              </div>

              {/* Operating Expenses */}
              <div style={{ padding: '12px 0 4px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ padding: '4px 22px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Operating Expenses</div>
                {(sections['Operating Expenses'] || []).map((line, i) => (
                  <SectionRow key={i} label={line.subcategory || line.category} amount={line.amount_cents} indent derived={line.derived} notes={line.notes} />
                ))}
                {(!sections['Operating Expenses'] || sections['Operating Expenses'].length === 0) && (
                  <div style={{ padding: '4px 36px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No operating expense entries for this period</div>
                )}
                <SectionRow label="Total Operating Expenses" amount={totals.operatingExpenses} bold borderTop />
              </div>

              {/* Net Income */}
              <div style={{ background: totals.netIncome >= 0 ? '#f0fdf4' : '#fef2f2', borderTop: '2px solid #111827' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 22px' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Net Income</div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 20, fontWeight: 900, color: totals.netIncome >= 0 ? '#166534' : '#991b1b', fontFamily: 'monospace' }}>
                      {totals.netIncome < 0 ? `(${fmt(Math.abs(totals.netIncome))})` : fmt(totals.netIncome)}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{fmtPct(totals.netMargin)} net margin</div>
                  </div>
                </div>
              </div>

              {/* Footer note */}
              <div style={{ padding: '10px 22px', fontSize: 10, color: '#9ca3af', borderTop: '1px solid #f3f4f6' }}>
                {plData?.source === 'xero'
                  ? 'Figures from Xero — audited source of record. Cached for 1 hour.'
                  : 'Includes manual entries + auto-derived from wire transactions and partner commissions. Connect Xero for audited figures.'}
              </div>
            </div>
          );
        })()}

        {!plLoading && !plData && (
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '32px', textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
            Select a time period above to load the P&amp;L
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
